import { ApolloClient, DataProxy, ExecutionPatchResult, NormalizedCacheObject, useLazyQuery, useMutation } from '@apollo/client';
import {
  CompletePortfolioData,
  CompletePortfolioVars,
  PortfolioData,
  PortfolioVars,
  UpdateMarkData,
  UpdateMarkVars,
  UpdatePortfolioStateData,
  UpdatePortfolioStateVars
} from '../gql-types';

import { ApiPortfolio } from '../../dashboard/models/ApiPortfolio';
import { ApiPortfolioState } from '../../dashboard/models/ApiPortfolioState';
import ApolloClientService from '../../shared/services/ApolloClientService';
import Config from '../../shared/config';
import GraphQL from '../gql';

let AssessmentApolloClient: ApolloClient<NormalizedCacheObject> = null;

export function usePortfolio(isFinalised: boolean = false) {
  if (!AssessmentApolloClient) {
    AssessmentApolloClient = ApolloClientService.create(Config.ASSESSMENT_GQL);
  }

  const [
    _portfolio,
    { loading: loadingPortfolio, error: portfolioError, data: portfolioData }
  ] = useLazyQuery<PortfolioData, PortfolioVars>(GraphQL.Query.PORTFOLIO, {
    client: AssessmentApolloClient,
    fetchPolicy: 'cache-and-network'
  });

  function getPortfolio(userkey: number, lessonkey: number, layer: number) {
    _portfolio({
      variables: {
        userkey,
        modulekey: lessonkey,
        layer
      }
    });
  }
  const [
    _updateMark,
    { loading: updatingMark, error: updateMarkError, data: updatedMarkData }
  ] = useMutation<UpdateMarkData, UpdateMarkVars>(
    GraphQL.Mutation.UPDATE_MARK,
    {
      client: AssessmentApolloClient
    }
  );

  function updateMark(
    userkey: number,
    modulekey: number,
    layer: number,
    componentkey: number,
    mark: number
  ) {
    _updateMark({
      variables: {
        userkey,
        modulekey,
        layer,
        componentkey,
        mark
      }
    });
  }

  const [_updatePortfolioState, { loading, error, data }] = useMutation<
    UpdatePortfolioStateData,
    UpdatePortfolioStateVars
  >(GraphQL.Mutation.UPDATE_PORTFOLIO_STATE, {
    client: AssessmentApolloClient,
    refetchQueries: [
      {
        query: GraphQL.Query.LIST_SUBMITTED_PORTFOLIOS,
        variables: { finalisedPortfolios: isFinalised }
      }
    ],
    awaitRefetchQueries: true
  });

  const updatePortfolioState = (
    userkey: number,
    modulekey: number,
    layer: number,
    state: ApiPortfolioState
  ) => {
    _updatePortfolioState({
      variables: {
        userkey,
        modulekey,
        layer,
        state
      }
    });
  };

  const [
    _completePortfolio,
    {
      loading: completingPortfolio,
      error: completePortfolioError,
      data: completePortfolioData
    }
  ] = useMutation<CompletePortfolioData, CompletePortfolioVars>(
    GraphQL.Mutation.COMPLETE_PORTFOLIO,
    {
      client: AssessmentApolloClient,
      update: (
        cache: DataProxy,
        { data }: ExecutionPatchResult<CompletePortfolioData>
      ) => {
        updatePortfolioCache(cache, data?.completePortfolio);
      }
    }
  );

  function completePortfolio(
    userkey: number,
    modulekey: number,
    layer: number
  ) {
    _completePortfolio({
      variables: {
        userkey,
        modulekey,
        layer
      }
    });
  }

  function updatePortfolioCache(cache: DataProxy, data: ApiPortfolio) {
    const updatedPortfolio = data;
    const { userkey, modulekey, layer } = updatedPortfolio;
    const portfolioCache = cache.readQuery<PortfolioData, PortfolioVars>({
      query: GraphQL.Query.PORTFOLIO,
      variables: {
        userkey,
        modulekey,
        layer
      }
    });

    if (!portfolioCache) {
      throw new Error(`Failed to get cached portfolio.
        userkey: ${userkey}, modulekey: ${modulekey}, layer: ${layer}`);
    }

    const portfolioCopy = { ...portfolioCache.portfolio, ...updatedPortfolio };

    cache.writeQuery<PortfolioData>({
      query: GraphQL.Query.PORTFOLIO,
      variables: {
        userkey,
        modulekey,
        layer
      },
      data: {
        portfolio: portfolioCopy
      }
    });
  }

  return {
    getPortfolio,
    loadingPortfolio,
    portfolioError,
    portfolio: portfolioData?.portfolio,
    updateMark,
    updatingMark,
    updateMarkError,
    updatedMark: updatedMarkData?.updateMark,
    updatePortfolioState,
    updatingPortfolioState: loading,
    updatePortfolioStateError: error,
    updatedPortfolio: data?.updatePortfolioState,
    completePortfolio,
    completingPortfolio,
    completePortfolioError,
    completedPortfolio: completePortfolioData?.completePortfolio
  };
}
